/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Single Page Layout - Responsive mobile/laptop (scrollable)
 * Updated: 2026-01-10
 */

import React, { useEffect, useState } from 'react';
import UploadWithOptions from './UploadWithOptions';
import TemplateCarousel from './TemplateCarousel';
import PrintTypeSelector from './PrintTypeSelector';
import ConfirmButton from './ConfirmButton';
import InteractivePreview from './InteractivePreview';

interface SinglePageLayoutProps {
  designOptions: any;
  setDesignOptions: (options: any) => void;
  selectedTemplate: any;
  setSelectedTemplate: (template: any) => void;
  templates: any[];
  onConfirm: () => Promise<void>;
  loading: boolean;
  canConfirm: boolean;
}

const SinglePageLayout: React.FC<SinglePageLayoutProps> = ({
  designOptions,
  setDesignOptions,
  selectedTemplate,
  setSelectedTemplate,
  templates,
  onConfirm,
  loading,
  canConfirm,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [uploadMode, setUploadMode] = useState<'image' | 'text'>('image');

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preview Node (Interactive) - No hidden CupPreview needed
  const interactivePreviewNode = (designOptions.imageBase64 && selectedTemplate) ? (
      <InteractivePreview
        cupTemplateUrl={selectedTemplate.url}
        maskUrl={selectedTemplate.maskUrl}
        logoImage={designOptions.imageBase64}
        printType={designOptions.printType || 'laser'}
        position={designOptions.position || { x: 0, y: 0 }}
        scale={designOptions.scale || 100}
        detailThreshold={designOptions.detailThreshold || 50}
        onPositionChange={(pos) =>
          setDesignOptions((prev: any) => ({
            ...prev,
            position: pos,
          }))
        }
        onScaleChange={(scale) =>
          setDesignOptions((prev: any) => ({
            ...prev,
            scale,
          }))
        }
        onDetailThresholdChange={(threshold) =>
          setDesignOptions((prev: any) => ({
            ...prev,
            detailThreshold: threshold,
          }))
        }
        onClear={() =>
          setDesignOptions((prev: any) => ({
            ...prev,
            imageBase64: '',
            imageId: '',
            imagePath: '',
          }))
        }
        disabled={loading}
      />
  ) : null;

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-full bg-gray-50 flex flex-col overflow-hidden relative">
        {/* Content Area - Balanced for one screen */}
        <div className="flex-1 flex flex-col p-1 gap-1 overflow-auto pb-16">
          {/* Upload & Preview Area - Responsive height based on uploadMode */}
          <div className={`${uploadMode === 'text' ? 'flex-1' : 'h-[45vh] flex-shrink-0'} min-h-[280px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible flex flex-col transition-all duration-200 relative`}>
            <UploadWithOptions
              key={designOptions.imageBase64 ? 'loaded' : 'empty'}
              onImageSelected={(imageData) =>
                setDesignOptions((prev: any) => ({
                  ...prev,
                  imageBase64: imageData.imageBase64,
                  imageId: imageData.imageId,
                  imagePath: imageData.imagePath,
                  detailThreshold: imageData.smartThreshold || 50,
                }))
              }
              onClear={() =>
                setDesignOptions((prev: any) => ({
                  ...prev,
                  imageBase64: '',
                  imageId: '',
                  imagePath: '',
                }))
              }
              onCustomPromptChange={(prompt) =>
                setDesignOptions((prev: any) => ({
                  ...prev,
                  customPrompt: prompt,
                }))
              }
              onModeChange={(mode) => setUploadMode(mode)}
              disabled={loading}
              customPrompt={designOptions.customPrompt || ''}
              previewComponent={interactivePreviewNode}
            />
          </div>

          {/* Controls Area - Responsive height based on uploadMode */}
          <div className={`${uploadMode === 'text' ? 'hidden' : 'flex-[1.5]'} flex flex-col gap-1 min-h-0 overflow-y-auto transition-all duration-200`}>
            {/* Hide Template/PrintType controls when in text mode to give full space */}
            {uploadMode !== 'text' && (
              <>
                <TemplateCarousel
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  onSelect={(template) => {
                      setSelectedTemplate(template);
                      setDesignOptions((prev: any) => ({
                      ...prev,
                      templateId: template.id,
                      }));
                  }}
                  disabled={loading}
                />

                {designOptions.imageBase64 && (
                   <PrintTypeSelector
                      printType={designOptions.printType || 'laser'}
                      onChange={(type) =>
                        setDesignOptions((prev: any) => ({
                          ...prev,
                          printType: type,
                        }))
                      }
                      disabled={loading}
                    />
                )}
              </>
            )}
          </div>
        </div>

        {/* Fixed Confirm Button at Bottom - Sticky */}
        {designOptions.imageBase64 && (
          <div className="fixed bottom-0 left-0 right-0 p-2 lg:p-3 bg-white border-t border-gray-200 shadow-lg z-[100]">
            <div className="max-w-full px-2">
              <ConfirmButton
                disabled={!canConfirm || loading}
                onClick={onConfirm}
                loading={loading}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Laptop Layout (2 columns - both full height)
  return (
    <div className="h-full bg-gray-50 flex overflow-hidden gap-0">
      {/* LEFT: Upload & Preview (70% - 1.2x taller content area) */}
      <div className="flex-[2.4] bg-white border-r border-gray-200 p-2 flex flex-col overflow-visible relative">
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-100 p-0.5 flex flex-col overflow-auto">
          <UploadWithOptions
            key={designOptions.imageBase64 ? 'loaded' : 'empty'}
            onImageSelected={(imageData) =>
              setDesignOptions((prev: any) => ({
                ...prev,
                imageBase64: imageData.imageBase64,
                imageId: imageData.imageId,
                imagePath: imageData.imagePath,
                detailThreshold: imageData.smartThreshold || 50,  // Use smart threshold from upload
              }))
            }
            onClear={() =>
              setDesignOptions((prev: any) => ({
                ...prev,
                imageBase64: '',
                imageId: '',
                imagePath: '',
              }))
            }
            onCustomPromptChange={(prompt) =>
              setDesignOptions((prev: any) => ({
                ...prev,
                customPrompt: prompt,
              }))
            }
            disabled={loading}
            customPrompt={designOptions.customPrompt || ''}
            previewComponent={interactivePreviewNode}
          />
        </div>
      </div>

      {/* RIGHT: Controls (30% - same height as left) */}
      <div className="flex-[1] bg-gray-50 p-2 lg:p-3 flex flex-col overflow-hidden">
        <div className="flex-1 space-y-3 overflow-y-auto pr-2 custom-scrollbar">
            <div className="flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Chọn mẫu ly yêu thích theo phong cách của bạn</h2>
            </div>

          {/* Template Carousel */}
          <TemplateCarousel
            templates={templates}
            selectedTemplate={selectedTemplate}
            onSelect={(template) => {
              setSelectedTemplate(template);
              setDesignOptions((prev: any) => ({
                ...prev,
                templateId: template.id,
              }));
            }}
            disabled={loading}
          />

          {/* Print Type */}
          {designOptions.imageBase64 && (
            <PrintTypeSelector
              printType={designOptions.printType || 'laser'}
              onChange={(type) =>
                setDesignOptions((prev: any) => ({
                  ...prev,
                  printType: type,
                }))
              }
              disabled={loading}
            />
          )}
        </div>

        {/* Confirm Button Sticky at Bottom Right */}
        {designOptions.imageBase64 && (
          <div className="flex-shrink-0 mt-auto pt-3 lg:pt-4 border-t border-gray-200">
            <ConfirmButton
              disabled={!canConfirm || loading}
              onClick={onConfirm}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SinglePageLayout;
